/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { observer } from 'mobx-react';
import React from 'react';
import { Redirect, Switch, useHistory } from 'react-router';
import { CluBuildSchemaPage } from 'src/clu/appShell/cluBuildSchemaPage/CluBuildSchemaPage';
import { CluDeployModelPage } from 'src/clu/appShell/cluDeployModelPage/CluDeployModelPage';
import { CluEntityDetailsPage } from 'src/clu/appShell/cluEntityDetailsPage/CluEntityDetailsPage';
import { CluModelDetailsListPage } from 'src/clu/appShell/CluModelDetailsListPage';
import { CluModelDetailsPage } from 'src/clu/appShell/CluModelDetailsPage';
import { CluProjectSettingsPage } from 'src/clu/appShell/CluProjectSettingsPage';
import { CluTagUtterancesPage } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesPage';
import { CluTestModelPage } from 'src/clu/appShell/cluTestModelsPage/CluTestModelPage';
import { CluTrainingPage } from 'src/clu/appShell/CluTrainingPage';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { createCluTrainingHandlerGroup } from 'src/clu/modules/handlers/cluTrainingHandlerGroup';
import { createCluGptHandlerGroup } from 'src/clu/modules/handlers/gpt/cluGptHandlerGroup';
import { useInitializeCluProject } from 'src/clu/modules/hooks/useInitializeCluProject';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { Page } from 'src/core/components/Page';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { createAzureOpenAiHandlerGroup } from 'src/core/handlers/azureOpenAiHandlerGroup';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useInstallHandlerGroup } from 'src/core/hooks/useInstallHandlerGroup';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';
import { routeTable } from 'src/core/utils/routeTable';
import { useModelEvaluationTracker } from 'src/modules/modelEvaluation/hooks/useModelEvaluationTracker';
import { useModelTrainingTracker } from 'src/modules/modelTraining/hooks/useModelTrainingTracker';
import { useModelTrainingUpdate } from 'src/modules/modelTraining/hooks/useModelTrainingUpdate';

type CluProjectPageProps = { projectName: string; serviceType: CluProjectType; csAccountPath: string };

export const CluProjectPage = observer((props: CluProjectPageProps) => {
    const { projectName, serviceType, csAccountPath } = props;

    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { isLoaded, isFailed } = useInitializeCluProject({ projectName, csAccountPath });

    useInstallHandlerGroup(dispatcher, createCluTrainingHandlerGroup, createAzureOpenAiHandlerGroup, createCluGptHandlerGroup);
    useModelTrainingTracker(rootStore.currentCluProjectStore?.trainingJobStoreHouse, jobId => {
        history.push(routeTable.clu.trainingPage(projectName), { selectedJobId: jobId });
        dispatcher.dispatch('hideFloatingToast', { id: jobId });
    });
    useModelTrainingUpdate({
        onUpdateTrainedModels: () => {
            rootStore.currentCluProjectStore.trainedModelStoreHouse.tracker.setState('notStarted');
            dispatcher.dispatch('getCluTrainedModels', []);
        },
        currentProjectStore: rootStore.currentCluProjectStore
    });

    useModelEvaluationTracker(rootStore.currentCluProjectStore?.evaluationJobStore, trainedModelName => {
        history.push(routeTable.clu.modelDetailsPage(rootStore.currentCluProjectStore?.id, trainedModelName));
    });

    if (isFailed) {
        return <Redirect to={routeTable[serviceType === 'Conversation' ? 'clu' : 'orchestration'].projectsPage()} />;
    }

    return isLoaded ? (
        <Switch>
            <PrivateRoute
                exact
                component={CluBuildSchemaPage}
                path={[routeTable.clu.buildSchemaPage(projectName), routeTable.orchestration.buildSchemaPage(projectName)]}
            />
            <PrivateRoute
                component={CluEntityDetailsPage}
                path={[routeTable.clu.entityDetailsPage(projectName), routeTable.orchestration.entityDetailsPage(projectName)]}
            />
            <PrivateRoute
                component={CluTagUtterancesPage}
                path={[routeTable.clu.dataTaggingPage(projectName), routeTable.orchestration.dataTaggingPage(projectName)]}
            />
            <PrivateRoute path={[routeTable.clu.trainingPage(), routeTable.orchestration.trainingPage()]} component={CluTrainingPage} />
            <PrivateRoute
                exact
                component={CluModelDetailsListPage}
                path={[routeTable.clu.modelDetailsListPage(), routeTable.orchestration.modelDetailsListPage()]}
            />
            <PrivateRoute
                component={CluModelDetailsPage}
                path={[routeTable.clu.modelDetailsPage(), routeTable.orchestration.modelDetailsPage()]}
            />
            <PrivateRoute
                exact
                component={CluTestModelPage}
                path={[routeTable.clu.testModelsPage(), routeTable.orchestration.testModelsPage()]}
            />
            <PrivateRoute
                component={CluDeployModelPage}
                path={[routeTable.clu.deployModelPage(), routeTable.orchestration.deployModelPage()]}
            />
            <PrivateRoute
                component={null}
                path={[routeTable.clu.reviewPredictionsPage(), routeTable.orchestration.reviewPredictionsPage()]}
            />
            <PrivateRoute path={[routeTable.clu.reviewEndpointPage(), routeTable.orchestration.reviewEndpointPage()]} component={null} />
            <PrivateRoute
                component={CluProjectSettingsPage}
                path={[routeTable.clu.projectSettingsPage(), routeTable.orchestration.projectSettingsPage()]}
            />

            <Redirect from={routeTable.clu.projectPage()} to={routeTable.clu.buildSchemaPage(projectName)} />
            <Redirect from={routeTable.orchestration.projectPage()} to={routeTable.orchestration.buildSchemaPage(projectName)} />
            <NotFoundRedirect />
        </Switch>
    ) : (
        <Page title={localize(globalMessages.loading)} pageName="CluProjectPage" size="container-fluid" animateIn={false}>
            <ShimmerFactory fullWidth includeHeaderShimmer shimmerCount={10} />
        </Page>
    );
});
